import React, { FC, useState, useCallback, useEffect } from 'react';
import styled, { css } from 'styled-components';
import noop from 'lodash/noop';

import { ClickToEditField, EditableField } from 'src/presentation/components/agent_desktop_components';
import { LongTextWithTooltip } from 'src/presentation/components/pages/shared/LongTextWithTooltip';

type EditableTokenNameType = {
  dataAid: string;
  onSave: Function;
  value: string;
};

const TokenNameDisplayer = ({ dataAid, isDisabled, onClick, value }) => (
  <StyledTokenNameDisplayer data-aid={`${dataAid}-displayer`} isDisabled={isDisabled} onClick={onClick}>
    <LongTextWithTooltip value={value} />
  </StyledTokenNameDisplayer>
);

const TokenNameEditor = ({ dataAid, initialValue, onBlur, onCancel }) => (
  <StyledEditableField
    autoFocus
    dataAid={`${dataAid}-editor`}
    onBlur={onBlur}
    onCancel={onCancel}
    onSubmit={noop} // prevents double saving on 'enter' press 'cause it triggers two events: submit and blur
    placeholder="Type Name"
    value={initialValue}
  />
);

const ClickToEditTokenName = ClickToEditField(TokenNameDisplayer, TokenNameEditor);

const EditableTokenName: FC<EditableTokenNameType> = ({ dataAid, onSave, value }) => {
  const [editableValue, setEditableValue] = useState(value);
  const [isResponsePending, setIsResponsePending] = useState(false);

  useEffect(() => {
    setEditableValue(value);
  }, [value, setEditableValue]);

  const onSaveFn = useCallback(
    async value => {
      setIsResponsePending(true);
      const saved = await onSave(value);
      if (saved) {
        setEditableValue(value);
      }
      setIsResponsePending(false);
    },
    [onSave, setIsResponsePending, setEditableValue]
  );

  return (
    <div data-aid={dataAid}>
      <ClickToEditTokenName
        dataAid={dataAid}
        isResponsePending={isResponsePending}
        onSave={onSaveFn}
        value={editableValue}
      />
    </div>
  );
};

export default EditableTokenName;

const inputCss = css`
  position: absolute;
  left: 1px;
  top: 7px;
  background: none;
  border: 1px solid transparent;
  box-sizing: border-box;
  border-radius: 4px;
  padding: 10px;
  text-align: left;
  width: calc(100% - 2px);
`;

const StyledTokenNameDisplayer = styled.button`
  ${inputCss};
  &:hover {
    background: ${p => p.theme.colors.white};
    border: 1px solid ${p => p.theme.colors.gray600};
    cursor: text;
  }
`;

const StyledEditableField = styled(EditableField)`
  ${inputCss};
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.gray600};
`;
